import { FilterField, Combo, AsyncHelper, PresetManager } from "@bryntum/gantt/gantt.module";
import { translate } from "./translate";

/**
 * @param params {{}}
 * @param params.generalConfigs
 * @param [params.HoursMoreThenZero] {boolean}
 * @param params.locale
 * @returns {{ref: string, icon: string, tooltip: string, toggleable: boolean, applyFilters: ((function(): Promise<void>)|*), text: string, type: string, menu: {onBeforeClose: menu.onBeforeClose, itemCls: string, anchor: boolean, layoutStyle: {flexDirection: string}, cls: string, type: string, onBeforeShow: menu.onBeforeShow}}}
 */
export const getFilters = function (params = {}) {
  return {
    type: "button",
    ref: "filters",
    icon: "b-fa b-fa-filter",
    text: "",
    tooltip: "Filters",
    toggleable: true,
    applyFilters: async () => {
      const filters = this.tbar.widgetMap.filters;
      const { PlanLevelFilter, TaskNameFilter, HoursMoreThenZero, AssignmentsFilter } = filters.menu.widgetMap;
      await AsyncHelper.sleep(10);
      PlanLevelFilter?.onChange({ value: PlanLevelFilter.value, userAction: true });
      HoursMoreThenZero?.onChange({ value: HoursMoreThenZero.value, userAction: true });
      AssignmentsFilter?.onChange({ value: AssignmentsFilter.value, userAction: true });
      if (TaskNameFilter?.value) {
        TaskNameFilter.setConfig({
          store: this.gantt.taskStore,
        });
        TaskNameFilter.onChange({ value: TaskNameFilter?.value });
      }
    },
    menu: {
      type: "popup",
      anchor: true,
      cls: "settings-menu",
      itemCls: "filterItem",
      layoutStyle: {
        flexDirection: "column",
      },
      onBeforeClose: ({ source: menu }) => {
        const { PlanLevelFilter, TaskNameFilter, HoursMoreThenZero, AssignmentsFilter } = menu.widgetMap;
        let badge = 0;
        if (PlanLevelFilter.value) {
          badge++;
        }
        if (TaskNameFilter.value) {
          badge++;
        }
        if (HoursMoreThenZero?.value === 1) {
          badge++;
        }
        if (AssignmentsFilter.value !== null) {
          badge++;
        }
        this.tbar.widgetMap.filters.badge = badge || null;
      },
      onBeforeShow: ({ source: menu }) => {
        const { PlanLevelFilter, TaskNameFilter, HoursMoreThenZero, AssignmentsFilter } = menu.widgetMap;
        if (!AssignmentsFilter) {
          menu.add(
            new Combo({
              items: [
                { text: translate("With assignments"), value: 1 },
                { text: translate("Without assignments"), value: 0 },
              ],
              ref: "AssignmentsFilter",
              placeholder: translate("Assignments"),
              clearable: true,
              editable: false,
              onChange: ({ value, userAction }) => {
                if (userAction) {
                  if (value === 1) {
                    this.gantt.taskStore.filter({
                      id: "assignmentsFilter",
                      filterBy: (task) => !!task.assignments?.length,
                    });
                  } else if (value === 0) {
                    this.gantt.taskStore.filter({
                      id: "assignmentsFilter",
                      filterBy: (task) => !task.assignments?.length,
                    });
                  } else {
                    this.gantt.taskStore.removeFilter("assignmentsFilter");
                  }
                }
              },
            })
          );
        }
        if (!HoursMoreThenZero && params.HoursMoreThenZero !== false) {
          menu.add(
            new Combo({
              items: [
                { text: translate("Yes"), value: 1 },
                { text: translate("No"), value: 0 },
              ],
              ref: "HoursMoreThenZero",
              placeholder: translate("Planned hours") + " > 0",
              clearable: true,
              editable: false,
              onChange: ({ value, userAction }) => {
                if (userAction) {
                  if (value) {
                    this.gantt.taskStore.filter({
                      property: "effort",
                      value: 0,
                      operator: ">",
                    });
                  } else {
                    this.gantt.taskStore.removeFilter("effort");
                  }
                }
              },
            })
          );
        }

        if (!PlanLevelFilter) {
          const { generalConfigs = {}, locale } = params;
          const levelsFromConfig = generalConfigs.projectLevels?.map((i) => ({ text: i[locale], value: i.index }));
          const items =
            levelsFromConfig && levelsFromConfig.length
              ? levelsFromConfig
              : [
                  { text: translate("Level 0 - Project"), value: 0 },
                  { text: translate("Level 1 - Order"), value: 1 },
                  { text: translate("Level 2 - Suborder"), value: 2 },
                  { text: translate("Level 3 - Job order"), value: 3 },
                  { text: translate("Level 4 - Job order detail work activity"), value: 4 },
                ];
          menu.add(
            new Combo({
              items: items,
              ref: "PlanLevelFilter",
              placeholder: translate("Filter by level"),
              clearable: true,
              editable: false,
              onChange: ({ value, userAction }) => {
                if (userAction) {
                  if (value) {
                    this.gantt.taskStore.filter("PlanLevel", value);
                  } else {
                    this.gantt.taskStore.removeFilter("PlanLevel");
                  }
                }
              },
            })
          );
        }
        if (!TaskNameFilter) {
          menu.add(
            new FilterField({
              store: this.gantt.taskStore,
              placeholder: translate("Task name"),
              ref: "TaskNameFilter",
              field: "name",
            })
          );
        } else {
          TaskNameFilter.setConfig({
            store: this.gantt.taskStore,
          });
        }
      },
    },
  };
};

export function getFeatures() {
  return {
    type: "button",
    ref: "featuresButton",
    icon: "b-fa b-fa-tasks",
    text: "",
    tooltip: translate("Toggle features"),
    toggleable: true,
    menu: {
      onItem: ({ source: item }) => {
        const { gantt } = this;

        if (item.feature) {
          const feature = gantt.features[item.feature];
          feature.disabled = !feature.disabled;
        } else if (item.indicator) {
          gantt.features.indicators.items[item.indicator] = !gantt.features.indicators.items[item.indicator];
        } else if (item.subGrid) {
          const subGrid = gantt.subGrids[item.subGrid];
          subGrid.collapsed = !subGrid.collapsed;
        }
      },
      onBeforeShow: ({ source: menu }) => {
        const { gantt } = this;

        menu.items.forEach((item) => {
          const { feature, subGrid } = item;
          if (feature) {
            // a feature might be not presented in the gantt
            // (the code is shared between "advanced" and "php" demos which use a bit different set of features)
            if (gantt.features[feature]) {
              item.checked = !gantt.features[feature].disabled;
            }
            // hide not existing features
            else {
              item.hide();
            }
          } else if (subGrid) {
            item.checked = gantt.subGrids[item.subGrid].collapsed;
          }
        });
        const indicators = menu.getItem("indicators");
        indicators?.menu?.items?.forEach((item) => {
          item.checked = !!gantt.features.indicators.items[item.indicator];
        });
      },
      items: [
        {
          text: translate("Draw dependencies"),
          feature: "dependencies",
          checked: false,
        },
        {
          text: translate("Task labels"),
          feature: "labels",
          checked: true,
        },
        {
          text: translate("Project lines"),
          feature: "projectLines",
          checked: false,
        },
        {
          text: translate("Highlight non-working time"),
          feature: "nonWorkingTime",
          checked: false,
        },
        {
          text: translate("Enable cell editing"),
          feature: "cellEdit",
          checked: false,
        },
        {
          text: translate("Show baselines"),
          feature: "baselines",
          checked: false,
        },
        {
          text: translate("Show rollup"),
          feature: "rollups",
          checked: false,
        },
        {
          text: translate("Show progress line"),
          feature: "progressLine",
          checked: false,
        },
        {
          text: translate("Hide schedule"),
          cls: "b-separator",
          subGrid: "normal",
          checked: false,
        },
        {
          text: translate("Indicators"),
          cls: "b-separator",
          id: "indicators",
          menu: {
            earlyDates: {
              text: translate("Early Dates"),
              indicator: "earlyDates",
              checked: true,
            },
            constraintDate: {
              text: translate("Constraint Date"),
              indicator: "constraintDate",
              checked: true,
            },
            lateDates: {
              text: translate("Late Dates"),
              indicator: "lateDates",
              checked: true,
            },
            deadlineDate: {
              text: translate("Deadline Date"),
              indicator: "deadlineDate",
              checked: true,
            },
            nextDependencyStartDate: {
              text: translate("Next dependency StartDate"),
              indicator: "nextDependencyStartDate",
              checked: true,
            },
            previousDependencyEndDate: {
              text: translate("Prev dependency EndDate"),
              indicator: "nextDependencyStartDate",
              checked: true,
            },
          },
        },
      ],
    },
  };
}

//First value is the bryntum preset key, second value is our translation key
export const presetsNames = {
  // manyYears: "Years",
  "year-30by100": "Years quarter",
  weekDateAndMonth: "Months",
  weekNumbersPreset: "Weeks number",
  weekAndDayLetter: "Week days",
  "weekAndDay-54by80": "Days",
};

export function getPresetsList() {
  return {
    type: "button",
    ref: "changePreset",
    icon: "b-fa b-fa-calendar-week",
    text: "",
    tooltip: translate("Change scheduler view"),
    menu: {
      onItem: ({ source: item }) => {
        (this.activeElement || this.gantt).viewPreset = item.presetId;
      },
      items: PresetManager.records.reduce((result, i) => {
        if (presetsNames[i.id]) {
          result[i.id] = { text: translate(presetsNames[i.id]), pressed: false, presetId: i.id };
        }
        return result;
      }, {}),
    },
  };
}
